// /Users/linoedge/project/welite/aptis-bag/bag/assets/src/components/Controllers/AudioAnalyser/VolumeAnalyser.js

/**
 * VolumeAnalyser
 *
 * Analyzes volume levels from a MediaStream using the Web Audio API.
 * Provides methods to update analysis, get calculated parameters (opacity, scale, speaking status),
 * and enable/disable the analysis process. Designed to be driven by an external loop.
 */
export default class VolumeAnalyser {
    /**
     * @param {object} [options={}]
     * @param {MediaStream} [options.stream=null] - The audio stream to analyze.
     * @param {AudioContext} [options.audioContext=null] - Optional shared AudioContext. If null, a local one is created.
     * @param {number} [options.speakingThreshold=5] - The average volume threshold to trigger the speaking state.
     * @param {function} [options.onUpdated=null] - Optional callback function called after each successful update, receiving { opacity, scale, isSpeaking }.
     * @param {function} [options.onSpeakingChanged=null] - Optional callback function called when speaking state changes.
     */
    constructor({ stream = null, audioContext = null, speakingThreshold = 5, onUpdated = null, onSpeakingChanged = null } = {}) {
        this.stream = stream;
        this.audioContext = audioContext;
        this.speakingThreshold = speakingThreshold;
        this.onUpdatedCallback = typeof onUpdated === 'function' ? onUpdated : null;
        this.onSpeakingCallback = typeof onSpeakingChanged === 'function' ? onSpeakingChanged : null;

        this.analyser = null;
        this.source = null;
        this.dataArray = null;
        this._isInternalContext = false; // Flag to track if we created the context
        this.isActive = false; // Is the analyser set up?
        this.isEnabled = true; // Can the analysis run?

        // Internal state updated by analysis
        this.currentMaxVolume = 0;
        this.currentAverageVolume = 0;

        // Scale range for indicator
        this.minScale = 0.9;
        this.maxScale = 1.0;

        this._setup();
    }

    /**
     * Sets up the AnalyserNode and connects the stream source.
     * @private
     */
    _setup() {
        // Prevent duplicate setup
        if (this.isActive) {
             console.warn("VolumeAnalyser: Setup already completed.");
             return;
        }
        // Ensure cleanup happens if setup fails mid-way
        try {
            if (!this.stream || !this.stream.getAudioTracks().length || !this.stream.active) {
                console.warn("VolumeAnalyser: Stream is missing, inactive, or has no audio tracks.");
                this._cleanup(); // Ensure partial setup is cleaned
                return;
            }

            if (!this.audioContext) {
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
                this._isInternalContext = true;
            } else {
                 this._isInternalContext = false;
            }

            if (this.audioContext.state === 'closed') {
                 console.error("VolumeAnalyser: Provided AudioContext is closed.");
                 this._cleanup();
                 return;
            }

            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 256;
            this.analyser.smoothingTimeConstant = 0.3;
            this.dataArray = new Uint8Array(this.analyser.frequencyBinCount);

            this.source = this.audioContext.createMediaStreamSource(this.stream);
            this.source.connect(this.analyser);

            this.isActive = true;
        } catch (error) {
            console.error("VolumeAnalyser: Error setting up analysis:", error);
            this._cleanup();
        }
    }

    /**
     * Performs one frame of audio analysis, updates internal volume states.
     * This should be called repeatedly from an external loop (e.g., requestAnimationFrame).
     * If the analyser is disabled via setEnabled(false), volumes will be set to 0.
     * Triggers the onUpdated callback if provided.
     */
    update() {
        if (!this.isActive || !this.analyser || !this.dataArray) {
            // Not properly set up, do nothing.
            this.currentMaxVolume = 0;
            this.currentAverageVolume = 0;
            return;
        }

        if (!this.isEnabled) {
            // If disabled, force volumes to 0
            if (this.currentMaxVolume !== 0 || this.currentAverageVolume !== 0) {
                 this.currentMaxVolume = 0;
                 this.currentAverageVolume = 0;
                 // Trigger update callback only when state changes to 0
                 if (this.onUpdatedCallback) {
                     this.onUpdatedCallback(this.getIndicatorParams());
                 }
            }
            return; // Don't perform analysis
        }

        // Perform analysis
        this.analyser.getByteFrequencyData(this.dataArray);

        let sum = 0;
        let max = 0;
        for (let i = 0; i < this.dataArray.length; i++) {
            const v = this.dataArray[i];
            sum += v;
            if (v > max) {
                max = v;
            }
        }
        this.currentMaxVolume = max;
        this.currentAverageVolume = this.dataArray.length > 0 ? sum / this.dataArray.length : 0;

        const isSpeaking = this.currentAverageVolume > this.speakingThreshold
        if (isSpeaking) {
            // If speaking, immediately show indicator and clear any pending timeouts
            if (this.speakingTimeout) {
                clearTimeout(this.speakingTimeout);
                this.speakingTimeout = null;
            }

            if (!this.isSpeakingState) {
                this.isSpeakingState = true;

                if (this.onSpeakingCallback) {
                    this.onSpeakingCallback(true);
                }
            }
        } else if (this.isSpeakingState && !this.speakingTimeout) {
            if (this.speakingTimeout) {
                clearTimeout(this.speakingTimeout);
            }

            // If not speaking but in speaking state, set timeout to turn off after 300ms
            this.speakingTimeout = setTimeout(() => {
                this.isSpeakingState = false;
                this.speakingTimeout = null;

                if (this.onSpeakingCallback) {
                    this.onSpeakingCallback(false);
                }
            }, 300);
        }

        // Trigger the callback with the latest parameters
        if (this.onUpdatedCallback) {
            this.onUpdatedCallback(this.getIndicatorParams());
        }
    }

    /**
     * Calculates and returns indicator parameters based on the current volume state.
     * @returns {{opacity: number, scale: number, isSpeaking: boolean}} - Calculated parameters.
     */
    getIndicatorParams() {
        const volumeRatio = Math.max(0, Math.min(1, this.currentMaxVolume / 255));
        // Use dynamic scale range
        const scale = this.minScale + volumeRatio * (this.maxScale - this.minScale);
        const opacity = 0.45 + volumeRatio * 0.55; // Keep opacity logic for now
        const isSpeaking = this.currentAverageVolume > this.speakingThreshold;

        return { opacity, scale, isSpeaking };
    }

    /**
     * Enables or disables the audio analysis.
     * When disabled, update() will not perform analysis and volumes will be treated as 0.
     * @param {boolean} enabled - True to enable, false to disable.
     * @returns {this} - Allows chaining.
     */
    setEnabled(enabled) {
        const shouldEnable = !!enabled; // Coerce to boolean

        // If enabling and not already active, attempt setup first.
        if (shouldEnable && !this.isActive) {
            this._setup();
             // If setup failed again, isActive will still be false, and update() will handle it.
        }

        this.isEnabled = shouldEnable;

        if (!this.isEnabled) {
            // Reset volumes immediately when disabling
            if (this.currentMaxVolume !== 0 || this.currentAverageVolume !== 0) {
                this.currentMaxVolume = 0;
                this.currentAverageVolume = 0;
                // Trigger update callback only when state changes to 0
                if (this.onUpdatedCallback) {
                    this.onUpdatedCallback(this.getIndicatorParams());
                }
            }
        }
        // No 'else' needed here; if enabling, the next update() call will perform analysis if isActive.
        return this;
    }

     /**
     * Cleans up resources used by this instance (disconnects nodes, closes internal AudioContext).
     * @private
     */
    _cleanup() {
        this.isActive = false;
        // No need to set isEnabled false here, it's a user control

        if (this.source) {
            try {
                this.source.disconnect();
            } catch (e) {
                console.warn("VolumeAnalyser: Error disconnecting source:", e);
            }
            this.source = null;
        }

        this.analyser = null; // GC will handle the node
        this.dataArray = null;

        // Close the AudioContext ONLY if we created it internally
        if (this._isInternalContext && this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close().then(() => {
                 console.log("VolumeAnalyser: Internal AudioContext closed.");
            }).catch(e => {
                 console.warn("VolumeAnalyser: Error closing internal AudioContext:", e);
            });
             this._isInternalContext = false; // Reset flag
        }
         // Nullify reference even if external, we are done with it in this instance
         this.audioContext = null;
         this.stream = null; // Nullify stream reference
    }

    /**
     * Destroys the analyser instance and cleans up all associated resources.
     */
    destroy() {
        this._cleanup();

        // Remove callback reference
        this.onUpdatedCallback = null;
        this.onSpeakingCallback = null;
    }

    // --- Setters for Chaining ---

    /**
     * Sets the MediaStream. If the analyser is already active,
     * it will cleanup and re-setup with the new stream.
     * @param {MediaStream} stream - The new audio stream.
     * @returns {this} - Allows chaining.
     */
    setStream(stream) {
         if (this.stream === stream) return this; // No change

         this.stream = stream;
         if (this.isActive) {
              this._cleanup(); // Cleanup old connections
              this._setup(); // Setup with new stream
         }
         return this;
    }

    /**
     * Sets the AudioContext. If the analyser is already active,
     * it will cleanup and re-setup with the new context.
     * Warning: Ensure the lifecycle of the external context is managed appropriately.
     * @param {AudioContext} audioContext - The new AudioContext.
     * @returns {this} - Allows chaining.
     */
    setAudioContext(audioContext) {
        if (this.audioContext === audioContext) return this; // No change

        // If we were using an internal context, make sure to close it first
        if (this._isInternalContext && this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close().catch(e => console.warn("VolumeAnalyser: Error closing previous internal AudioContext:", e));
             this._isInternalContext = false;
        }

        this.audioContext = audioContext;
        if (this.isActive) {
            // Cleanup necessary parts related to the old context
             if (this.source) {
                 try { this.source.disconnect(); } catch(e) {}
                 this.source = null;
             }
             this.analyser = null;
             this.isActive = false; // Mark as inactive before setup
             // Re-setup with the new context
             this._setup();
        }
        return this;
    }

    /**
     * Sets the speaking threshold.
     * @param {number} threshold - The new threshold value.
     * @returns {this} - Allows chaining.
     */
    setSpeakingThreshold(threshold) {
        this.speakingThreshold = typeof threshold === 'number' ? threshold : 5;
        return this;
    }

    /**
     * Sets the update callback function.
     * @param {function | null} callback - The new callback function or null.
     * @returns {this} - Allows chaining.
     */
    setOnUpdated(callback) {
        this.onUpdatedCallback = typeof callback === 'function' ? callback : null;
        return this;
    }

    /**
     * Sets the speaking change callback function.
     * @param {function | null} callback - The new callback function or null.
     * @returns {this} - Allows chaining.
     */
    setOnSpeakingChanged(callback) {
        this.onSpeakingCallback = typeof callback === 'function' ? callback : null;
        return this;
    }

    /**
     * Sets the minimum and maximum scaling factors directly.
     * Use this if you want to specify the exact scale range (e.g., 0.8 to 1.1).
     * @param {number} minFactor - The minimum scale factor. Must be less than maxFactor.
     * @param {number} maxFactor - The maximum scale factor. Must be greater than minFactor.
     * @returns {this} - Allows chaining.
     */
    setScaleFactors(minFactor, maxFactor) {
        if (typeof minFactor !== 'number' || typeof maxFactor !== 'number' || minFactor >= maxFactor) {
            // Reset to defaults
            this.minScale = 0.9;
            this.maxScale = 1.0;
        } else {
            this.minScale = minFactor;
            this.maxScale = maxFactor;
        }
        return this;
    }

    /**
     * Sets the scaling range based on minimum and maximum pixel values.
     * Calculates minScale as minPx / maxPx and sets maxScale to 1.0.
     * The final scale will be calculated as: (minPx / maxPx) + volumeRatio * (1 - minPx / maxPx).
     * @param {number} minPx - The minimum size in pixels (e.g., 180). Must be positive and less than maxPx.
     * @param {number} maxPx - The maximum size in pixels (e.g., 190). Must be positive and greater than minPx.
     * @returns {this} - Allows chaining.
     */
    setScaleRangeFromPixels(minPx, maxPx) {
        if (typeof minPx !== 'number' || typeof maxPx !== 'number' || minPx <= 0 || maxPx <= 0 || minPx >= maxPx) {
            // Reset to defaults
            this.minScale = 0.9;
            this.maxScale = 1.0;
        } else {
            this.minScale = minPx / maxPx;
            this.maxScale = 1.0; // Max scale corresponds to maxPx
        }
        return this;
    }
}
